/**
 * Stable per-org color palette.
 *
 * No `color` column on Organization yet — derive deterministically from the
 * org ID (or fall back to name). Same org always gets the same palette.
 */

export interface OrgPalette {
  /** Shown as the banner base fill. */
  base: string;
  /** Three blob colors for the mesh gradient, bright-to-dim. */
  blobs: [string, string, string];
  /** Solid accent used for the initials avatar border. */
  accent: string;
}

const PALETTES: OrgPalette[] = [
  {
    // Cyan / violet / blue
    base: "#0f172a",
    blobs: ["#06b6d4", "#8b5cf6", "#3b82f6"],
    accent: "#06b6d4",
  },
  {
    // Amber / rose / red
    base: "#1c1210",
    blobs: ["#f59e0b", "#ec4899", "#dc2626"],
    accent: "#f59e0b",
  },
  {
    // Emerald / teal / cyan
    base: "#0a1a16",
    blobs: ["#10b981", "#14b8a6", "#06b6d4"],
    accent: "#10b981",
  },
  {
    // Violet / indigo / pink
    base: "#161024",
    blobs: ["#8b5cf6", "#6366f1", "#ec4899"],
    accent: "#8b5cf6",
  },
  {
    // Rose / orange / amber
    base: "#1f1014",
    blobs: ["#f43f5e", "#f97316", "#f59e0b"],
    accent: "#f43f5e",
  },
  {
    // Blue / sky / teal
    base: "#0b1629",
    blobs: ["#3b82f6", "#0ea5e9", "#14b8a6"],
    accent: "#3b82f6",
  },
  {
    // Lime / emerald / cyan
    base: "#111d0f",
    blobs: ["#84cc16", "#10b981", "#06b6d4"],
    accent: "#84cc16",
  },
  {
    // Fuchsia / pink / violet
    base: "#1d0c1a",
    blobs: ["#d946ef", "#ec4899", "#a855f7"],
    accent: "#d946ef",
  },
];

/**
 * Fast deterministic hash (FNV-1a 32-bit). Good enough for picking a palette.
 */
function fnv1a(str: string): number {
  let hash = 2166136261;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = (hash * 16777619) >>> 0;
  }
  return hash;
}

export function paletteForOrg(seed: string | null | undefined): OrgPalette {
  const key = (seed ?? "").trim() || "_default";
  const index = fnv1a(key) % PALETTES.length;
  return PALETTES[index]!;
}

export function initialsFor(name: string | null | undefined): string {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return ((parts[0]![0] ?? "") + (parts[parts.length - 1]![0] ?? "")).toUpperCase();
}
